export interface TaskMonitorEvent {
  status: 'processing' | 'extracting menu' | 'completed' | 'failed'
  progress?: number
  message?: string
  data?: any
}

export type TaskMonitorCallback = (event: TaskMonitorEvent) => void

/**
 * SSE task monitor with production-ready error handling
 *
 * @param url - SSE endpoint URL
 * @param onMessage - Callback for task events
 * @param onError - Callback for connection errors
 * @param reconnectInterval - milliseconds before reconnect (default: 3000)
 * @returns Cleanup function
 * 
 * @example
 * const cleanup = monitorTask(
 *   'https://api.example.com/task/monitor?taskId=123',
 *   (event) => console.log('Status:', event.status),
 *   (error) => console.error('Error:', error)
 * )
 * 
 * // Later, to stop monitoring:
 * cleanup()
 */
export const monitorTask = (
  url: string,
  onMessage: TaskMonitorCallback,
  onError?: (error: Error) => void,
  reconnectInterval = 3000
): (() => void) => {
  let eventSource: EventSource | null = null
  let stopped = false
  let hasReceivedDone = false

  // Separate close connection (doesn't mark as stopped)
  const closeConnection = () => {
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }
  }

  const connect = () => {
    if (stopped) return

    eventSource = new EventSource(url)

    // Handle 'connected' event from server
    eventSource.addEventListener('connected', (event: Event) => {
      try {
        JSON.parse((event as MessageEvent).data)
      } catch (err) {
        onError?.(new Error('Invalid connected event payload'))
      }
    })

    // Handle 'progress' event from server
    eventSource.addEventListener('progress', (event: Event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data)

        // Parse message first
        const message = data.message?.toLowerCase()

        // Map message to status (message takes priority over data.status field)
        switch (message) {
          case 'processing':
            onMessage({ status: 'processing', message: data.message })
            break
          case 'extracting menu':
            onMessage({ status: 'extracting menu', message: data.message })
            break
          case 'completed':
            onMessage({ status: 'completed', message: data.message })
            break
          case 'failed':
            onMessage({ status: 'failed', message: data.message })
            break
          default:
            // Default to processing for unknown messages
            onMessage({
              status: 'processing',
              message: data.message || 'Processing'
            })
        }
      } catch (err) {
        onError?.(new Error('Invalid progress event payload'))
      }
    })

    // Handle 'done' event from server (indicates normal completion)
    eventSource.addEventListener('done', () => {
      hasReceivedDone = true
      closeConnection()
    })

    // Handle connection-level errors
    eventSource.onerror = () => {
      // If we received 'done' event, this is normal connection closure
      if (hasReceivedDone) {
        closeConnection()
        return
      }

      // Otherwise, it's an actual error
      if (stopped) return

      closeConnection()
      onError?.(new Error('SSE connection error'))

      if (!stopped) {
        setTimeout(connect, reconnectInterval)
      }
    }
  }

  // Cleanup function stops SSE and reconnect attempts
  const cleanup = () => {
    stopped = true
    closeConnection()
  }

  // Start first connection
  connect()

  return cleanup
}
